import { addDays } from 'date-fns'

import { StopLoss } from '@App/interfaces/CommonInterfaces'
import { Button } from '@App/ui/crypto_exchange/desktop/components/controls/Button'

import { Order } from '@Redux/slices/trade/trades/types'

import {
  ITradeBlockProps,
  ORDER_ACTION_TYPE,
} from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderTrade/components/types'

import { TradeInput } from '../../../TradeInput'
import {
  COMMON_FIELD_NAMES,
  roundNumber,
  StopLossField,
  TakeProfitField,
  useOrderData,
  useOrderForm,
  useOrderFormCalculations,
} from '../../model'
import { DateInput } from '../DateInput'
import { Disclosure } from '../Disclosure'
import { Divider } from '../Divider'
import { InputHint } from '../InputHint'
import { LimitOrderBlockView } from '../LimitOrderBlockView'
import { SeparateLine } from '../SeparateLine'
import { StopLossFields } from '../StopLossFields'
import { TakeProfitFields } from '../TakeProfitFields'

import { CancelOrderButton } from './components/CancelOrderButton'
import { GeneralInfo } from './components/GeneralInfo'
import { PaddingContainer } from './components/PaddingContainer'
import { PositionDetailsPopUp } from './components/PositionDetailsPopUp'
import { useEditOrder } from './model'
import { IConfigMarket } from '@Redux/interfaces'

export const EditOrder = ({
  current_market,
  operationType,
  order,
  orderType,
}: ITradeBlockProps & {
  operationType: ORDER_ACTION_TYPE
  order: Order
  current_market: IConfigMarket
}) => {
  const { target_currency, source_currency, amount_precision, price_precision } = current_market
  const LEVERAGE = order.leverage
  const {
    wallet,
    walletSymbol,
    marketRule,
    tickerDataPrice,
    operationTypeDisplay,
    tickerToWalletCurrencyPrice,
    oppositeTickerDataPrice,
    isLogin,
  } = useOrderData({
    source_currency,
    target_currency,
    operationType,
  })

  const {
    register,
    formState,
    setValue,
    getValues,
    handleSubmit,
    triggerPrice,
    triggerPriceActive,
    setTriggerPriceValue,
    cancelationDate,
    stopLossType,
    stopLossAmount,
    stopLossDistance,
    stopLossDistancePerCent,
    stopLossLevel,
    takeProfitAmount,
    takeProfitDistance,
    takeProfitDistancePerCent,
    takeProfitLevel,
    takeProfitActive,
    stopLossField,
    setStopLossField,
    takeProfitField,
    setTakeProfitField,
    setTakeProfitActive,
    setStopLossType,
    reset,
    getStopLossValues,
    getTakeProfitValues,
  } = useOrderForm({
    amountPrecision: amount_precision,
    pricePrecision: price_precision,
    oppositeTickerDataPrice,
    tickerToWalletCurrencyPrice,
    operationType,
    tickerDataPrice,
    marketRule,
    isEdit: true
  })

  const {
    calculateTakeProfitValuesByActiveField,
    calculateTakeProfitValuesInit,
    calculateStopLossValuesByActiveField,
    calculateStopLossValuesInit,
    calculateOnTriggerPriceUpdate,
  } = useOrderFormCalculations({
    marketRule,
    oppositeTickerDataPrice,
    tickerDataPrice,
    tickerToWalletCurrencyPrice,
    getValues,
    setValue,
    leverage: LEVERAGE,
    takeProfitField,
    stopLossField,
    walletAmount: wallet?.amount,
    operationType,
    amountPrecision: amount_precision,
    pricePrecision: price_precision,
    isEdit: true,
  })

  const { onSubmit } = useEditOrder({
    order,
    wallet,
    orderType,
    getValues,
    setValue,
    reset,
    getTakeProfitValues,
    getStopLossValues,
  })

  return (
    <>
      <LimitOrderBlockView
        isLogin={isLogin}
        current_market={current_market}
        formBtn={
          <PaddingContainer>
            <Button onClick={handleSubmit(onSubmit)} disabled={!formState.isDirty && formState.isValid}>
              Update Order
            </Button>
          </PaddingContainer>
        }
      >
        <GeneralInfo
          quantity={order.quantity}
          tickerDataPrice={tickerDataPrice}
          leverage={LEVERAGE ?? 1}
          operationType={operationType}
          currency={order.currency}
          triggerPrice={triggerPrice ?? undefined}
          renderDetailsPopUp={(margin) => (
            <PositionDetailsPopUp
              leverage={LEVERAGE ?? 0}
              currency={order.currency}
              margin={margin}
              targetPrice={order.amount}
            />
          )}
        />
        <CancelOrderButton order={order} />

        <SeparateLine />
        <PaddingContainer>
          <Disclosure
            title={`${operationTypeDisplay} when price is`}
            isOpen={triggerPriceActive}
            onChange={(val) =>
              val
                ? setTriggerPriceValue(undefined, calculateStopLossValuesByActiveField)
                : setTriggerPriceValue(null, calculateStopLossValuesByActiveField)
            }
          >
            <TradeInput
              key={'trigger_price'}
              type="number"
              showControls
              {...register('trigger_price', {
                valueAsNumber: true,
              })}
              onUpdateValue={calculateOnTriggerPriceUpdate}
              error={formState.errors.trigger_price?.message}
            />
            <DateInput
              text="Add cancelation date"
              value={cancelationDate}
              onClick={() => {
                setValue('cancellation_date', addDays(new Date(), 1))
              }}
              onChange={(value) => {
                setValue('cancellation_date', value ?? null, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            />
          </Disclosure>
          <Divider />
          <StopLossFields
            stopLossField={stopLossField}
            onOpenChange={(val) => setStopLossType(val ? StopLoss.Normal : null, calculateStopLossValuesInit)}
            stopLossType={stopLossType}
            onStopLossFieldChange={(val) => setStopLossField(val as StopLossField)}
            onStopLossTypeChange={(val) => setStopLossType(val as StopLoss, calculateStopLossValuesInit)}
            input={
              <TradeInput
                key={stopLossField}
                type="number"
                showControls
                {...register(stopLossField, { valueAsNumber: true })}
                onUpdateValue={() => {
                  calculateStopLossValuesByActiveField()
                }}
                targetCurrency={stopLossField === 'stop_loss_amount' ? walletSymbol : ''}
                error={formState.errors[stopLossField]?.message}
                subValue={
                  stopLossField === 'stop_loss_distance'
                    ? `${roundNumber(stopLossDistancePerCent ?? 0 * 100, 3)}%`
                    : undefined
                }
              />
            }
            inputHint={
              <InputHint
                distance={stopLossDistance}
                distancePerCent={stopLossDistancePerCent}
                amount={stopLossAmount}
                level={stopLossLevel}
                walletSymbol={walletSymbol}
                activeField={COMMON_FIELD_NAMES[stopLossField]}
              />
            }
          />
          <Divider />
          <TakeProfitFields
            onOpenChange={(val) => setTakeProfitActive(val, calculateTakeProfitValuesInit)}
            takeProfitField={takeProfitField}
            takeProfitActive={takeProfitActive}
            onTakeProfitFieldChange={(val) => setTakeProfitField(val as TakeProfitField)}
            input={
              <TradeInput
                key={takeProfitField}
                type="number"
                showControls
                targetCurrency={takeProfitField === 'take_profit_amount' ? walletSymbol : ''}
                {...register(takeProfitField, { valueAsNumber: true })}
                onUpdateValue={() => {
                  calculateTakeProfitValuesByActiveField()
                }}
                error={formState.errors[takeProfitField]?.message}
                subValue={
                  takeProfitField === 'take_profit_distance'
                    ? `${roundNumber(takeProfitDistancePerCent ?? 0 * 100, 3)}%`
                    : undefined
                }
              />
            }
            inputHint={
              <InputHint
                distance={takeProfitDistance}
                distancePerCent={takeProfitDistancePerCent}
                amount={takeProfitAmount}
                level={takeProfitLevel}
                walletSymbol={walletSymbol}
                activeField={COMMON_FIELD_NAMES[takeProfitField]}
                amountFieldName="Profit"
              />
            }
          />
          <Divider />
        </PaddingContainer>
      </LimitOrderBlockView>
    </>
  )
}
